import type { Analytics } from 'firebase/analytics';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useCookieConsent } from 'web/components/CookieConsentContext';
import errorReporter from 'web/utils/error-reporter';
import { isClientSide } from 'web/utils/ssr';
import { shouldUseEmulators } from './common';
import FirebaseContext from './FirebaseContext';

const isStorybook = process.env.STORYBOOK === 'true';
type AnalyticsModule = typeof import('firebase/analytics');
type AnalyticsApi = {
  logEvent: (eventName: string, eventData?: Record<string, unknown>) => void;
  setCurrentScreen: (screenName: string | undefined) => void;
  setUserId: (userId: string) => void;
};

let analyticsModulePromise: Promise<AnalyticsModule> | null = null;
let initializedAnalytics: Analytics | null = null;
let initializedAnalyticsApi: AnalyticsApi | null = null;
let activeAnalyticsConsumers = 0;
let replayPageViewOnActivation = false;

const loadAnalyticsModule = () => {
  analyticsModulePromise = analyticsModulePromise || import('firebase/analytics');
  return analyticsModulePromise;
};

const disableAnalytics = () => {
  if (activeAnalyticsConsumers === 0 && initializedAnalytics && analyticsModulePromise) {
    void analyticsModulePromise
      .then(({ setAnalyticsCollectionEnabled }) => {
        if (activeAnalyticsConsumers === 0) {
          setAnalyticsCollectionEnabled(initializedAnalytics, false);
        }
      })
      .catch((err) => errorReporter.report(new Error(`Failed to disable firebase analytics: ${err}`)));
  }
};

const useAnalytics = () => {
  const firebase = useContext(FirebaseContext);
  const { measurementAllowed } = useCookieConsent();
  const measurementEnabled = isClientSide && !shouldUseEmulators && !isStorybook && measurementAllowed;
  const measurementEnabledRef = useRef(measurementEnabled);
  measurementEnabledRef.current = measurementEnabled;
  const [analyticsApi, setAnalyticsApi] = useState<AnalyticsApi | null>(() =>
    measurementEnabled ? initializedAnalyticsApi : null,
  );
  const guardedAnalyticsApi = useMemo<AnalyticsApi | null>(
    () =>
      analyticsApi
        ? {
            logEvent: (eventName, eventData) => {
              if (measurementEnabledRef.current) analyticsApi.logEvent(eventName, eventData);
            },
            setCurrentScreen: (screenName) => {
              if (measurementEnabledRef.current) analyticsApi.setCurrentScreen(screenName);
            },
            setUserId: (userId) => {
              if (measurementEnabledRef.current) analyticsApi.setUserId(userId);
            },
          }
        : null,
    [analyticsApi],
  );

  useEffect(() => {
    if (!measurementEnabled) {
      setAnalyticsApi(null);
      disableAnalytics();
      return;
    }

    if (activeAnalyticsConsumers === 0 && initializedAnalytics) {
      replayPageViewOnActivation = true;
    }
    activeAnalyticsConsumers += 1;
    let active = true;
    void loadAnalyticsModule()
      .then((analyticsModule) => {
        if (!active || !measurementEnabledRef.current) {
          return;
        }

        try {
          initializedAnalytics = initializedAnalytics || analyticsModule.getAnalytics(firebase);
          analyticsModule.setAnalyticsCollectionEnabled(initializedAnalytics, true);
          initializedAnalyticsApi = initializedAnalyticsApi || {
            logEvent: (eventName, eventData) => analyticsModule.logEvent(initializedAnalytics, eventName, eventData),
            setCurrentScreen: (screenName) => analyticsModule.setCurrentScreen(initializedAnalytics, screenName),
            setUserId: (userId) => analyticsModule.setUserId(initializedAnalytics, userId),
          };
          if (replayPageViewOnActivation) {
            replayPageViewOnActivation = false;
            analyticsModule.logEvent(initializedAnalytics, 'page_view', {
              page_path: window.location.pathname,
            });
          }
          setAnalyticsApi(initializedAnalyticsApi);
        } catch (err) {
          errorReporter.report(new Error(`Failed to initialize firebase analytics: ${err}`));
        }
      })
      .catch((err) => {
        if (active) {
          errorReporter.report(new Error(`Failed to load firebase analytics: ${err}`));
        }
      });

    return () => {
      active = false;
      activeAnalyticsConsumers = Math.max(0, activeAnalyticsConsumers - 1);
      disableAnalytics();
    };
  }, [firebase, measurementEnabled]);

  return measurementEnabled ? guardedAnalyticsApi : null;
};

export default useAnalytics;
